import useClientData from 'modules/clients/hooks/useClientData';
import { PageTemplate } from 'modules/shared/features/content';
import { GoogleMap, useGoogleMapScriptLoader } from 'modules/shared/features/google-map';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ClientMapClientViewControlsPanel from '../ClientMapViewControlsPanel/ClientMapViewControlsPanel';
import useClientUpdateBasicForm from 'modules/clients/hooks/useClientUpdateBasicForm';
import { useMutation, useQuery } from '@tanstack/react-query';
import { updateClientMap } from '@api/services/client.service';
import { IClient, IMapPayload, IProject } from '@api/models';
import { toast } from 'react-toastify';
import { getProjectsLeanByClientId } from '@api/services/project.service';
import MapProjectMarker from '../MapProjectMarker/MapProjectMarker';
// import toast from 'react-hot-toast'; // <- or whatever you use

interface ClientMapProps {
    client?: IClient | null;
    isLoading: boolean;
    setData: (updater: any) => void; // keep your existing signature
}

const DEFAULT_VIEW = {
    center: { lat: 0, lng: 0 },
    zoom: 6,
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
const round6 = (n: number) => Math.round(n * 1e6) / 1e6;

const ClientMap = (props: ClientMapProps) => {
    // const { client._id } = useParams();
    const { client, isLoading, setData } = props;

    const { isLoaded } = useGoogleMapScriptLoader();

    const mapRef = useRef<google.maps.Map | null>(null);

    const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const zoomListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const centerListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const savedView = useMemo(() => {
        const lat = Number(client?.preferences?.mapCenter?.latitude);
        const lng = Number(client?.preferences?.mapCenter?.longitude);
        const z = client?.preferences?.mapZoom;

        return {
            center: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : DEFAULT_VIEW.center,
            zoom: typeof z === 'number' && Number.isFinite(z) ? z : DEFAULT_VIEW.zoom,
        };
    }, [client?._id]);

    /**
     * IMPORTANT: Your GoogleMap wrapper recreates the map instance when center/zoom props change.
     * So: pass fixed initial props once, then control view via mapRef imperatively.
     */
    const initialCenterRef = useRef<google.maps.LatLngLiteral>(savedView.center);
    const initialZoomRef = useRef<number>(savedView.zoom);

    const [isPicking, setIsPicking] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const [latitude, setLatitude] = useState(String(savedView.center.lat));
    const [longitude, setLongitude] = useState(String(savedView.center.lng));
    const [zoom, setZoom] = useState(String(savedView.zoom));

    const [markerPos, setMarkerPos] = useState<google.maps.LatLngLiteral>(savedView.center);

    const isEditingZoomRef = useRef(false);

    const clientId = client?._id;

    const updateClientMapMutation = useMutation({
        mutationFn: (payload: IMapPayload) => {
            if (!clientId) throw new Error('Client not loaded');
            return updateClientMap(clientId, payload);
        },
    });

    const { data: projects, isLoading: projectIsLoading } = useQuery<IProject[]>(
        ['projects', clientId],
        () => getProjectsLeanByClientId(clientId as string),
        { enabled: Boolean(clientId) }
    );

    // reset state when saved view changes (client loaded)
    useEffect(() => {
        setLatitude(String(savedView.center.lat));
        setLongitude(String(savedView.center.lng));
        setZoom(String(savedView.zoom));
        setMarkerPos(savedView.center);
        setHasUnsavedChanges(false);
        setIsPicking(false);

        if (mapRef.current) {
            mapRef.current.panTo(savedView.center);
            mapRef.current.setZoom(savedView.zoom);
        }
    }, [savedView]);

    const updateFromPosition = useCallback((pos: google.maps.LatLngLiteral) => {
        const lat = round6(pos.lat);
        const lng = round6(pos.lng);

        setMarkerPos({ lat, lng });
        setLatitude(String(lat));
        setLongitude(String(lng));
        setHasUnsavedChanges(true);
    }, []);

    const handleLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;

        clickListenerRef.current?.remove();
        zoomListenerRef.current?.remove();
        centerListenerRef.current?.remove();

        zoomListenerRef.current = map.addListener('zoom_changed', () => {
            if (isEditingZoomRef.current) return; // 🚫 don't override typing

            const z = map.getZoom();
            if (z == null) return;

            setZoom(z.toFixed(2));
            setHasUnsavedChanges(true);
        });

        centerListenerRef.current = map.addListener('center_changed', () => {
            const c = map.getCenter();
            if (!c) return;
            setLatitude(String(round6(c.lat())));
            setLongitude(String(round6(c.lng())));
            setHasUnsavedChanges(true);
        });
    }, []);

    // pick mode: click on map to set lat/lng (listener only while picking)
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        clickListenerRef.current?.remove();
        clickListenerRef.current = null;

        if (!isPicking) return;

        clickListenerRef.current = map.addListener('click', (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return;
            const pos = e.latLng.toJSON();

            updateFromPosition(pos);
            map.panTo(pos);

            // If you want marker to stay while picking, DON'T turn this off.
            // If you want pick-once behavior, keep it:
            setIsPicking(false);
        });

        return () => {
            clickListenerRef.current?.remove();
            clickListenerRef.current = null;
        };
    }, [isPicking, updateFromPosition]);

    useEffect(() => {
        return () => {
            clickListenerRef.current?.remove();
            zoomListenerRef.current?.remove();
            centerListenerRef.current?.remove();
        };
    }, []);

    const panToCurrentInputs = useCallback(
        (nextLatStr?: string, nextLngStr?: string) => {
            const map = mapRef.current;
            if (!map) return;

            const lat = Number(nextLatStr ?? latitude);
            const lng = Number(nextLngStr ?? longitude);

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
            if (lat < -90 || lat > 90) return;
            if (lng < -180 || lng > 180) return;

            const pos = { lat, lng };
            map.panTo(pos);
            setMarkerPos(pos);
        },
        [latitude, longitude]
    );

    const onLatitudeChange = useCallback(
        (v: string) => {
            setLatitude(v);
            setHasUnsavedChanges(true);
            panToCurrentInputs(v, undefined);
        },
        [panToCurrentInputs]
    );

    const onLongitudeChange = useCallback(
        (v: string) => {
            setLongitude(v);
            setHasUnsavedChanges(true);
            panToCurrentInputs(undefined, v);
        },
        [panToCurrentInputs]
    );

    const onZoomChange = useCallback((v: string) => {
        isEditingZoomRef.current = true; // 🟡 user is typing

        setZoom(v);
        setHasUnsavedChanges(true);

        const map = mapRef.current;
        if (!map) return;

        const z = parseFloat(v);
        if (!Number.isFinite(z)) return;

        map.setZoom(clamp(Math.round(z), 1, 22));
    }, []);

    const onMarkerDragEnd = useCallback(
        (pos: google.maps.LatLngLiteral) => {
            updateFromPosition(pos);
            mapRef.current?.panTo(pos);
        },
        [updateFromPosition]
    );

    const onReset = useCallback(() => {
        setLatitude(String(savedView.center.lat));
        setLongitude(String(savedView.center.lng));
        setZoom(String(savedView.zoom));
        setMarkerPos(savedView.center);
        setHasUnsavedChanges(false);
        setIsPicking(false);

        if (mapRef.current) {
            mapRef.current.panTo(savedView.center);
            mapRef.current.setZoom(savedView.zoom);
        }
    }, [savedView]);

    // ✅ integrated submit: build values, optimistic setData, call submit, then setData(updated)
    const handleSubmit = useCallback(async () => {
        if (!client) return;

        const lat = Number(latitude);
        const lng = Number(longitude);
        const z = clamp(Math.round(Number(zoom)), 1, 22);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        if (lat < -90 || lat > 90) return;
        if (lng < -180 || lng > 180) return;

        const values = {
            ...client,
            preferences: {
                ...client.preferences,
                mapZoom: z,
                mapCenter: {
                    ...client.preferences.mapCenter,
                    latitude: lat,
                    longitude: lng,
                },
            },
        };

        // optimistic UI update
        setData((prev: any) => ({
            ...prev,
            preferences: values.preferences,
        }));

        try {
            const payload = {
                mapCenter: values.preferences.mapCenter,
                mapZoom: values.preferences.mapZoom,
            };
            await updateClientMapMutation.mutateAsync(payload);
            toast.success('Client Map Updated');

            // keep local state in sync with server response
            // setData(updatedClient);

            setHasUnsavedChanges(false);
            // toast.success('Client updated successfully.');
        } catch (ex: any) {
            // toast.error(ex?.data?.message || 'Some error occured, please try again.');
            console.log(ex);

            // optional: revert to savedView (or refetch)
            // onReset();
        }
    }, [client, latitude, longitude, zoom, setData, onReset]);

    const canRenderMap = Boolean(clientId) && isLoaded && !projectIsLoading;

    return (
        <>
            {canRenderMap && (
                <div className="relative w-full h-[500px] rounded-lg ">
                    <GoogleMap center={initialCenterRef.current} zoom={initialZoomRef.current} onLoad={handleLoad}>
                        {/* Project Markers */}
                        {(projects ?? []).map((project) => (
                            <MapProjectMarker key={project._id} project={project} />
                        ))}
                        {isPicking && (
                            <GoogleMap.AdvancedMarker
                                position={markerPos}
                                title="Click / drag to change location"
                                draggable
                                onMarkerDragEnd={onMarkerDragEnd}
                                zIndex={20}
                            >
                                <div className="relative">
                                    <div className="w-8 h-8 bg-primary rounded-full border-4 border-white shadow-lg flex items-center justify-center animate-pulse">
                                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path
                                                fillRule="evenodd"
                                                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-6 bg-red-500" />
                                </div>
                            </GoogleMap.AdvancedMarker>
                        )}
                    </GoogleMap>

                    <ClientMapClientViewControlsPanel
                        title="Map Preference"
                        isPicking={isPicking}
                        onTogglePick={() => setIsPicking((v) => !v)}
                        latitude={latitude}
                        longitude={longitude}
                        zoom={zoom}
                        onLatitudeChange={onLatitudeChange}
                        onLongitudeChange={onLongitudeChange}
                        onZoomChange={onZoomChange}
                        hasUnsavedChanges={hasUnsavedChanges}
                        onReset={onReset}
                        // ✅ Save now uses handleSubmit
                        onSave={handleSubmit}
                        isSubmitting={isLoading || updateClientMapMutation.isPending}
                        onZoomFocus={() => {
                            isEditingZoomRef.current = true;
                        }}
                        onZoomBlur={() => {
                            isEditingZoomRef.current = false;

                            const z = parseFloat(zoom);
                            if (Number.isFinite(z)) {
                                setZoom(clamp(Math.round(z), 1, 22).toFixed(2));
                            }
                        }}
                    />
                </div>
            )}
        </>
    );
};

export default ClientMap;
